import React, { useState, useEffect } from 'react';
import { FiSearch, FiEye, FiMail, FiPhone, FiX, FiCalendar, FiAlertCircle, FiCheckCircle, FiXCircle, FiKey, FiEyeOff, FiUsers, FiMapPin, FiRefreshCw, FiFilter, FiChevronDown, FiChevronUp, FiArrowUp, FiArrowDown, FiMoreVertical, FiSettings, FiShield, FiAward, FiTrendingUp, FiActivity, FiBarChart2, FiPieChart, FiTarget, FiZap, FiClock, FiGlobe, FiStar, FiCreditCard, FiPackage, FiShoppingCart, FiDollarSign, FiTrendingDown, FiDownload, FiUpload, FiEdit2, FiTrash2, FiEye2, FiPlus, FiMinus, FiChevronRight, FiChevronLeft, FiHome, FiTruck, FiNavigation } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorOwnerManagement = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOwners, setTotalOwners] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetOwner, setResetOwner] = useState(null);

  useEffect(() => {
    fetchVendorOwners();
  }, [search, currentPage, itemsPerPage]);

  const fetchVendorOwners = async (isManualRefresh = false) => {
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (search) params.search = search;

      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing vendor owners...', { id: 'refresh' });
      }

      console.log('Fetching vendor owners with params:', params);
      const response = await api.get('/superadmin/vendor-owners', { params });
      console.log('Vendor owners response:', response.data);
      
      if (response.data.success) {
        setOwners(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalOwners(response.data.total || 0);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} vendor owners`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load vendor owners data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch vendor owners error:', error);
      toast.error(`Failed to fetch vendor owners: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewOwnerDetails = async (owner) => {
    try {
      const response = await api.get(`/superadmin/vendor-owners/${owner._id}`);
      if (response.data.success) {
        setSelectedOwner(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch owner details');
    }
  };

  const openPasswordResetModal = (owner) => {
    setResetOwner(owner);
    setShowPasswordModal(true);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordReset = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const response = await api.post(`/superadmin/vendor-owners/${resetOwner._id}/reset-password`, {
        newPassword
      });
      
      if (response.data.success) {
        toast.success('Password reset successfully');
        setShowPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
        setResetOwner(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Vendor Owners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <FiUsers className="text-white text-xl" />
                </div>
                Vendor Owners Management
              </h1>
              <p className="text-gray-600 mt-1">Manage vendor owners who registered with email</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold flex items-center gap-2">
                <FiUsers className="text-teal-600" />
                {totalOwners} Owners
              </div>
              <button
                onClick={() => fetchVendorOwners(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Search Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[250px] relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Owners Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Owner Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Restaurant Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Contact Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Restaurants</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Joined Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {owners.map(owner => (
                <tr key={owner._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 font-medium text-gray-900">{owner.name}</td>
                  <td className="px-6 py-4 text-gray-700">{owner.restaurantName}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FiMail size={14} className="text-gray-500" />
                      <span className="text-gray-700">{owner.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FiPhone size={14} className="text-gray-500" />
                      <span className="text-gray-700">{owner.contactNumber}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{owner.city}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                      {owner.vendorCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FiCalendar size={14} className="text-gray-500" />
                      <span className="text-gray-700">{new Date(owner.createdAt).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => viewOwnerDetails(owner)}
                        className="group relative px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                        title="View Owner Details"
                      >
                        <FiEye size={14} className="group-hover:scale-110 transition-transform" />
                        <span>View</span>
                      </button>
                      <button 
                        onClick={() => openPasswordResetModal(owner)}
                        className="group relative px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                        title="Reset Password"
                      >
                        <FiKey size={14} className="group-hover:rotate-12 transition-transform" />
                        <span>Reset</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {owners.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMail className="text-gray-400 text-2xl" />
            </div>
            <p className="text-gray-500">No vendor owners found</p>
          </div>
        )}
      </div>

        {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {owners.length} of {totalOwners} vendor owners
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="group px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-sm font-medium hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <FiChevronLeft className="group-hover:-translate-x-0.5 transition-transform" />
                Previous
              </button>
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                if (
                  pageNum === 1 || 
                  pageNum === totalPages || 
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg ${
                        pageNum === currentPage 
                          ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-500/25' 
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                  return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="group px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-sm font-medium hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                Next
                <FiChevronRight className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-gray-600">per page</span>
            </div>
        </div>
        </div>
      )}

      {/* Owner Details Modal */}
      {showModal && selectedOwner && (
  <div
    className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={() => setShowModal(false)}
  >
    <div
      className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          Vendor Owner Details
        </h2>

        <button
          onClick={() => setShowModal(false)}
          className="p-2 rounded-lg hover:bg-gray-100 transition"
        >
          <FiX size={22} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Owner Information */}
        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-800 mb-4">
            Owner Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
              <p className="text-gray-500">Name</p>
              <p className="font-medium">{selectedOwner.name}</p>
            </div>

            <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium">{selectedOwner.email}</p>
            </div>

            <div>
              <p className="text-gray-500">Contact</p>
              <p className="font-medium">{selectedOwner.contactNumber}</p>
            </div>
          </div>
        </div>

        {/* Restaurant Information */}
        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-800 mb-4">
            Restaurant Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
              <p className="text-gray-500">Restaurant</p>
              <p className="font-medium">{selectedOwner.restaurantName}</p>
            </div>

            <div>
              <p className="text-gray-500">City</p>
              <p className="font-medium">{selectedOwner.city}</p>
            </div>

            <div className="md:col-span-2">
              <p className="text-gray-500">Address</p>
              <p className="font-medium">
                {selectedOwner.address || "Not provided"}
              </p>
            </div>

            <div>
              <p className="text-gray-500">Joined</p>
              <p className="font-medium">
                {new Date(selectedOwner.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {selectedOwner.rejectedRestaurants > 0 && (
            <div className="mt-5 flex items-center gap-3 bg-red-50 border border-red-200 p-4 rounded-xl">
              <FiXCircle className="text-red-600 text-xl" />
              <span className="text-red-700 font-semibold">
                {selectedOwner.rejectedRestaurants} Rejected Restaurants
              </span>
            </div>
          )}
        </div>

        {/* Associated Restaurants */}
        <div className="bg-white border rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FiMapPin className="text-teal-600" />
            Associated Restaurants ({selectedOwner.vendors?.length || 0})
          </h3>

          {selectedOwner.vendors?.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Restaurant</th>
                    <th className="px-4 py-3 text-left">City</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Active</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {selectedOwner.vendors.map((vendor) => (
                    <tr key={vendor._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">
                        {vendor.restaurantName || "N/A"}
                      </td>

                      <td className="px-4 py-3">
                        {vendor.city || "N/A"}
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            vendor.approvalStatus === "approved"
                              ? "bg-green-100 text-green-700"
                              : vendor.approvalStatus === "pending"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {vendor.approvalStatus}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            vendor.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {vendor.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              No vendors associated with this owner
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end">
        <button
          onClick={() => setShowModal(false)}
          className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}


      {/* Password Reset Modal */}
      {showPasswordModal && resetOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Owner Name:</strong> {resetOwner.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {resetOwner.email}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button 
                onClick={handlePasswordReset}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <FiKey className="group-hover:rotate-12 transition-transform" />
                Reset Password
              </button>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
  );
};

export default VendorOwnerManagement;
